import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { PropertyService } from '../../services/property.service';
import { AuthService } from '../../../../core/services/auth.service';
import { Address, Amenity, Property, PropertyAmenity, PropertyCalendar, PropertyImage, Timezone, UserProperty } from '../../models/property.model';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, filter, forkJoin, map, Observable, of, shareReplay, Subject, switchMap, take, takeUntil, tap, throwError, withLatestFrom } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DisplayNameEdit } from '../property-edit-fields/display-name-edit/display-name-edit';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { SnackBarService } from '../../../../shared/services/snackbar.service';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { AddressEdit } from '../property-edit-fields/address-edit/address-edit';
import { DescriptionEdit } from '../property-edit-fields/description-edit/description-edit';
import { PropertyDetailsEdit } from '../property-edit-fields/property-details-edit/property-details-edit';
import { MatChipsModule } from '@angular/material/chips';
import { AmenitiesEdit } from '../property-edit-fields/amenities-edit/amenities-edit';
import { CalendarsEdit } from '../property-edit-fields/calendars-edit/calendars-edit';
import { ImagesEdit } from '../property-edit-fields/images-edit/images-edit';
import { TimezoneEdit } from '../property-edit-fields/timezone-edit/timezone-edit';
import { LoaderComponent } from '../../../../shared/general/components/loader/loader.component';
import { AmenityService } from '../../services/amenity.service';
import { SeoService } from '../../../../shared/services/seo.service';

interface DialogData {
  propertyId: string;
  currentAmenities: PropertyAmenity[];   // what the property already has
}
interface PcDialogData {
  propertyId: string;
  currentCalendars: PropertyCalendar[];   // what the property already has
}
@Component({
  selector: 'app-property-edit',
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    MatProgressSpinner,
    MatSnackBarModule,
    MatChipsModule,
    LoaderComponent
],
  templateUrl: './property-edit.html',
  styleUrl: './property-edit.scss',
})
export class PropertyEditComponent implements OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private amenityService = inject(AmenityService);
  private seo = inject(SeoService);
  private snackbar = inject(SnackBarService);

  @Input() userProperties: UserProperty[] | undefined;
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);
  private propertyService = inject(PropertyService);
  loading = false;
  isTimezonesLoaded = false;
  timezones: Timezone[] = [];
  categories: string[] = [];
  private destroy$ = new Subject<void>();
  property$!: Observable<Property | null>;
  ownership$: Observable<{ allowed: boolean }> = combineLatest([
    this.auth.user$.pipe(filter(user => user !== null)),
    this.route.paramMap
  ]).pipe(
    take(1), // Only check once
    switchMap(([user, params]) => {
      const propertyId = params.get('id');

      if (!propertyId || !user) {
        return of({ allowed: false });
      }

      if (user.role === 'admin') {
        return of({ allowed: true });
      }

      return this.propertyService.getUserProperties(user.id).pipe(
        map(properties => ({
          allowed: properties.some(p => p.propertyId === propertyId)
        })),
        take(1)
      );
    })
  );

  ngOnInit() {
    this.loading = true;
    this.categories = this.amenityService.getAmenityCategories();
    this.setTimezones();
    this.property$ = this.route.paramMap.pipe(
      switchMap(params => {
        const id = params.get('id');
        this.loading = false;
        return id ? this.propertyService.getPropertyStream(id) : of(null);
      }),
      shareReplay(1)
    );
    this.seo.updateMetaTags("Edit Property");
  }

  goBack = () => {
    this.router.navigate(['/profile/manage-properties']);
  }

  setTimezones = () => {
    this.propertyService.getTimezones().subscribe({
      next: (timezones) => {
        console.log('timezones', timezones);
        this.timezones = timezones;
        this.isTimezonesLoaded = true;
      },
      error: (err) => console.error('Error loading timezones', err)
    });
  };

  getCategoryIcon(category: string) {
    return this.amenityService.getCategoryIcon(category);
  }

  // amenitiesByCategory$(category: string): Observable<Amenity[]> {
  //   return this.property$.pipe(
  //     map(property => {
  //       if (!property?.amenities) return [];
  //       return property.amenities.filter(a => a.category === category);
  //     }),
  //     shareReplay(1) // optional: cache last result
  //   );
  // }

  getAmenitiesForCategory(property: Property, category: string): Amenity[] {
    if (!property?.amenities) return [];
    return property.amenities.filter(a => a.category === category);
  }

  private getChangedFields(
    original: Property,
    edited: Property
  ): Partial<Property> {
    const changes: Partial<Property> = {};

    // Helper: deep compare (handles objects, arrays, primitives)
    const isDifferent = (a: any, b: any): boolean => {
      return JSON.stringify(a) !== JSON.stringify(b);
    };

    // Top-level fields
    const keys = Object.keys(edited) as (keyof Property)[];
    for (const key of keys) {
      const oldVal = original[key];
      const newVal = edited[key];

      if (key === 'address') {
        // Handle nested address
        if (oldVal && newVal && typeof oldVal === 'object' && typeof newVal === 'object') {
          const addrChanges = this.getChangedFields(oldVal as any, newVal as any);
          if (Object.keys(addrChanges).length > 0) {
            changes.address = addrChanges as Address;
          }
        } else if (isDifferent(oldVal, newVal)) {
          changes.address = newVal as Address;
        }
      }
      // Skip ID, createdAt, etc. if you don't want to send them
      else if (key === 'id' || key === 'createdAt' || key === 'updatedAt') {
        continue;
      }
      // All other fields
      else if (isDifferent(oldVal, newVal)) {
        (changes as any)[key] = newVal;
      }
    }

    return changes;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  editDisplayName = (currentValue: string) => {
    const dialogRef = this.dialog.open(DisplayNameEdit, {
      data: currentValue,
      panelClass: ['property-edit-dialog', 'display-name-edit'],
      width: '460px'
    });

    dialogRef.componentInstance.save
      .pipe(
        withLatestFrom(this.property$),
        switchMap(([newName, property]) => {
          if (!property?.id) return of(null);
          return this.propertyService.updateProperty(property.id, { displayName: newName });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (updated) => {
          if (updated) {
            this.snackbar.open(
              `✅ Display name updated to "${updated.displayName}"`,
              ' ',
              { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
            );
          }
          dialogRef.close();
        },
        error: () => {
          this.snackbar.open(
            '⚠️ Failed to update display name',
            ' ',
            { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
          );
        }
      });

    dialogRef.componentInstance.cancel.subscribe(() => {
      console.log('Cancelled');
      dialogRef.close();
    });
  }

  editTimezone = (currentValue: string) => {
    console.log('editTimezone', currentValue);
    const dialogRef = this.dialog.open(TimezoneEdit, {
      data: { currentValue: currentValue, timezones: this.timezones },
      panelClass: ['property-edit-dialog', 'timezone-edit'],
      width: '460px'
    });

    dialogRef.componentInstance.save
      .pipe(
        withLatestFrom(this.property$),
        switchMap(([newTimezone, property]) => {
          console.log('newTimezone', newTimezone);
          const newTz = this.timezones.find(t => t.ianaName == newTimezone);
          console.log('newTz', newTz);
          if (!newTz) return of(null);

          console.log({ newTimezone, property });
          if (!property?.id) return of(null);
          return this.propertyService.updateProperty(property.id, { timezone: newTz });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (updated) => {
          if (updated) {
            this.snackbar.open(
              `✅ Timezone updated to "${updated.timezone?.displayName}"`,
              ' ',
              { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
            );
          }
          dialogRef.close();
        },
        error: () => {
          this.snackbar.open(
            '⚠️ Failed to update timezone',
            ' ',
            { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
          );
        }
      });

    dialogRef.componentInstance.cancel.subscribe(() => {
      console.log('Cancelled');
      dialogRef.close();
    });
  }

  editAddress = (currentValue: Address) => {
    const dialogRef = this.dialog.open(AddressEdit, {
      data: currentValue,
      panelClass: ['property-edit-dialog', 'address-edit'],
      width: '480px'
    });

    dialogRef.componentInstance.save
      .pipe(
        withLatestFrom(this.property$),
        switchMap(([newAddress, property]) =>
          this.propertyService.getLatLong(newAddress).pipe(
            tap(([lat, lng]) => {
              newAddress.lat = lat;
              newAddress.lng = lng;
            }),
            map(() => ({ newAddress, property }))
          )
        ),
        catchError(err => {
          this.snackbar.open(
            `⚠️ Geocoding failed: ${err.message}`,
            ' ',
            {
              duration: 5000, 
              panelClass: ['error-snackbar'],
              horizontalPosition: 'center',
              verticalPosition: 'top'
           }
          );
          // Still allow save without lat/lng
          return of({ newAddress: null, property: null });
        }),
        switchMap(({ newAddress, property }) => {
          if (!property?.id || !newAddress) return of(null);

          const payload: Address = {
            id: newAddress.id,
            street: newAddress.street,
            city: newAddress.city,
            state: newAddress.state,
            zipCode: newAddress.zipCode,
            country: "USA",
            lat: newAddress.lat,
            lng: newAddress.lng
          };

          return this.propertyService
            .updateAddress(property.id, payload)
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (updated) => {
          if (updated) {
            this.snackbar.open(
              `✅ Address updated successfully`,
              ' ',
              { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
            );
          }
          dialogRef.close();
        },
        error: () => {
          this.snackbar.open(
            '⚠️ Failed to update address',
            ' ',
            { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
          );
        }
      });

    dialogRef.componentInstance.cancel.subscribe(() => {
      console.log('Cancelled');
      dialogRef.close();
    });
  }

  editDescription = (currentValue: string) => {
    const dialogRef = this.dialog.open(DescriptionEdit, {
      data: currentValue,
      panelClass: ['property-edit-dialog', 'description-edit'],
      width: '480px'
    });

    dialogRef.componentInstance.save
      .pipe(
        withLatestFrom(this.property$),
        switchMap(([newDescription, property]) => {
          if (!property?.id) return of(null);
          return this.propertyService.updateProperty(property.id, { description: newDescription });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (updated) => {
          if (updated) {
            this.snackbar.open(
              `✅ Description updated`,
              ' ',
              { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
            );
          }
          dialogRef.close();
        },
        error: () => {
          this.snackbar.open(
            '⚠️ Failed to update display name',
            ' ',
            { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
          );
        }
      });

    dialogRef.componentInstance.cancel.subscribe(() => {
      console.log('Cancelled');
      dialogRef.close();
    });
  }

  editPropertyDetails = (currentValue: Property) => {
    const dialogRef = this.dialog.open(PropertyDetailsEdit, {
      data: { ...currentValue },
      panelClass: ['property-edit-dialog', 'details-edit'],
      width: '480px'
    });

    dialogRef.componentInstance.save
      .pipe(
        withLatestFrom(this.property$), // get latest from cache
        switchMap(([editedProperty, cachedProperty]) => {
          if (!cachedProperty?.id) return of(null);

          // Compute diff: only changed fields
          const changes = this.getChangedFields(cachedProperty, editedProperty);

          if (Object.keys(changes).length === 0) {
            // Nothing changed → close dialog
            dialogRef.close();
            return of(cachedProperty);
          }

          // Optimistic update
          const optimistic = { ...cachedProperty, ...changes };
          this.propertyService.updateProperty(cachedProperty.id, optimistic);

          return this.propertyService.updateProperty(cachedProperty.id, changes).pipe(
            tap(updated => {
              // Success: update cache with server response
              this.propertyService.updateProperty(cachedProperty.id, updated);
            }),
            catchError(err => {
              // Rollback
              this.propertyService.updateProperty(cachedProperty.id, cachedProperty);
              this.propertyService.refresh(cachedProperty.id);
              return throwError(() => err);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.snackbar.open('✅ Property updated', ' ', 
              { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' });
          }
          dialogRef.close();
        },
        error: () => {
          this.snackbar.open('⚠️ Update failed', ' ', 
            { duration: 5000, panelClass: ['success-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' });
        }
      });

      dialogRef.componentInstance.cancel.subscribe(() => {
        console.log('Cancelled');
        dialogRef.close();
      });
  };

  editAmenities = (_: Amenity[]) => {
    this.property$.pipe(take(1)).subscribe((property) => {
      if (!property?.id) return;

      forkJoin({
        allAmenities: this.propertyService.getAmenities(),
        propertyAmenities: this.propertyService.getPropertyAmenities(property.id),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ allAmenities, propertyAmenities }) => {
          const ref = this.dialog.open(AmenitiesEdit, {
            width: '650px',
            panelClass: ['property-edit-dialog', 'amenities-edit'],
            data: {
              propertyId: property.id,
              currentAmenities: propertyAmenities,
            } as DialogData,
          });

          // Save → refresh enriched cache
          ref.componentInstance.save
            .pipe(takeUntil(this.destroy$))
            .subscribe((selectedIds) => {
              this.propertyService.refresh(property.id); // <-- KEY: reload enriched data
              this.snackbar.open('✅ Amenities saved', '', {
                duration: 4000,
                panelClass: ['success-snackbar'],
                horizontalPosition: 'center',
                verticalPosition: 'top',
              });
              ref.close();
            });

          ref.componentInstance.cancel.subscribe(() => ref.close());
        });
    });
  };

  editCalendars = () => {
    this.property$.pipe(take(1)).subscribe((property) => {
      if (!property?.id) return;
      property.calendars?.map(c => c.propertyId = property.id);
      const calendars = Array.isArray(property.calendars) ? property.calendars : [];

      console.log('calendars', calendars);

      const ref = this.dialog.open(CalendarsEdit, {
        width: '900px',
        panelClass: ['property-edit-dialog', 'calendars-edit'],
        data: {
          propertyId: property.id,
          currentCalendars: calendars, // ← match interface
        } as PcDialogData,
      });

      ref.componentInstance.save.subscribe(() => {
        this.propertyService.refresh(property.id);
        ref.close();
      });

      ref.componentInstance.cancel.subscribe(() => ref.close());
    });
  }

  editImages = (currentValue: PropertyImage[]) => {
    this.property$.pipe(take(1)).subscribe((property) => {
      if (!property?.id) return;
      const dialogRef = this.dialog.open(ImagesEdit, {
        data: { propertyImages: currentValue, propertyId: property.id },
        panelClass: ['property-edit-dialog', 'images-edit'],
        width: '480px'
      });

      dialogRef.afterClosed().subscribe((updatedImages: PropertyImage[]) => {
        if (updatedImages) {
          // THIS IS THE KEY: Update the local array with the server's response
          property.images = [...updatedImages]; 
          this.cdr.detectChanges();
          // If you are using ChangeDetectionStrategy.OnPush, 
          // you might need to manually trigger detection:
          // this.cdr.detectChanges(); 
        }
      });
    });

    // dialogRef.componentInstance.cancel.subscribe(() => {
    //   console.log('Cancelled');
    //   dialogRef.close();
    // });
  }
}
